/**
 * DynamicStyles - Simplified CSS Variables for theming
 * Fonts now loaded via <link> in index.html
 */

import { useLayoutEffect, useMemo } from 'react';
import { useThemeStore } from '../../stores/themeStore';

function setCSSVariable(name: string, value: string | undefined, defaultValue: string) {
  try {
    const root = document.documentElement;
    if (!root) return;
    const finalValue = (value !== undefined && value !== null && value !== '') ? value : defaultValue;
    root.style.setProperty(name, finalValue);
  } catch (e) {
    // silent
  }
}

function applyThemeVariables(theme: any) {
  try {
    if (!theme) return;

    const colors = theme.colors;
    const glass = theme.glass as any;
    const card = theme.card;
    const navigation = theme.navigation;
    const typography = theme.typography;

    setCSSVariable('--color-bg-primary', colors?.surface || '#ffffff', '#ffffff');
    setCSSVariable('--color-bg-secondary', colors?.background || '#f8fafc', '#f8fafc');
    setCSSVariable('--color-surface', colors?.surface || '#ffffff', '#ffffff');
    setCSSVariable('--color-surface-elevated', colors?.surfaceElevated || '#ffffff', '#ffffff');
    setCSSVariable('--glass-bg-primary', colors?.surface || '#ffffff', '#ffffff');
    setCSSVariable('--glass-bg-secondary', colors?.background || '#f8fafc', '#f8fafc');
    
    const bgGradient = glass?.enabled 
      ? `linear-gradient(135deg, ${glass.background} 0%, ${colors?.background} 100%)`
      : colors?.background;
    setCSSVariable('--glass-bg-gradient', bgGradient, colors?.background);
    setCSSVariable('--color-background', colors?.background, '#f8fafc');
    
    setCSSVariable('--glass-card-bg', card?.background || 'rgba(255,255,255,0.95)', 'rgba(255,255,255,0.95)');
    setCSSVariable('--glass-card-border', card?.border || '1px solid #e5e7eb', '1px solid #e5e7eb');
    setCSSVariable('--glass-sidebar-bg', navigation?.sidebar?.background || colors?.surface, colors?.surface);
    setCSSVariable('--glass-sidebar-text-color', colors?.text, colors?.text);
    setCSSVariable('--glass-header-bg', navigation?.header?.background || colors?.surface, colors?.surface);

  // === TYPOGRAPHY ===
  if (typography) {
    setCSSVariable('--font-family', typography.fontFamily || 'Inter, system-ui, sans-serif', 'Inter, system-ui, sans-serif');
    setCSSVariable('--font-family-heading', typography.fontFamilyHeading || 'Inter, system-ui, sans-serif', 'Inter, system-ui, sans-serif');
    setCSSVariable('--font-family-mono', typography.fontFamilyMono || 'JetBrains Mono, monospace', 'JetBrains Mono, monospace');
    setCSSVariable('--font-size-base', typography.baseFontSize || '16px', '16px');
    
    // Headings
    setCSSVariable('--font-size-h1', typography.h1?.fontSize || '2.5rem', '2.5rem');
    setCSSVariable('--font-weight-h1', typography.h1?.fontWeight || '700', '700');
    setCSSVariable('--font-color-h1', typography.h1?.color || '#000000', '#000000');
    setCSSVariable('--line-height-h1', typography.h1?.lineHeight || '1.2', '1.2');
    
    setCSSVariable('--font-size-h2', typography.h2?.fontSize || '2rem', '2rem');
    setCSSVariable('--font-weight-h2', typography.h2?.fontWeight || '600', '600');
    setCSSVariable('--font-color-h2', typography.h2?.color || '#000000', '#000000');
    setCSSVariable('--line-height-h2', typography.h2?.lineHeight || '1.3', '1.3');
    
    setCSSVariable('--font-size-h3', typography.h3?.fontSize || '1.75rem', '1.75rem');
    setCSSVariable('--font-weight-h3', typography.h3?.fontWeight || '600', '600');
    setCSSVariable('--font-color-h3', typography.h3?.color || '#000000', '#000000');
    
    setCSSVariable('--font-size-h4', typography.h4?.fontSize || '1.5rem', '1.5rem');
    setCSSVariable('--font-weight-h4', typography.h4?.fontWeight || '500', '500');
    setCSSVariable('--font-color-h4', typography.h4?.color || '#000000', '#000000');
    
    setCSSVariable('--font-size-h5', typography.h5?.fontSize || '1.25rem', '1.25rem');
    setCSSVariable('--font-weight-h5', typography.h5?.fontWeight || '500', '500');
    setCSSVariable('--font-color-h5', typography.h5?.color || '#000000', '#000000');
    
    setCSSVariable('--font-size-h6', typography.h6?.fontSize || '1rem', '1rem');
    setCSSVariable('--font-weight-h6', typography.h6?.fontWeight || '500', '500');
    setCSSVariable('--font-color-h6', typography.h6?.color || '#000000', '#000000');
    
    // Body text
    setCSSVariable('--font-size-body', typography.body?.fontSize || '1rem', '1rem');
    setCSSVariable('--font-weight-body', typography.body?.fontWeight || '400', '400');
    setCSSVariable('--line-height-body', typography.body?.lineHeight || '1.6', '1.6');
    
    setCSSVariable('--font-size-body-small', typography.bodySmall?.fontSize || '0.875rem', '0.875rem');
    setCSSVariable('--font-size-caption', typography.caption?.fontSize || '0.75rem', '0.75rem');
    
    // Card specific
    setCSSVariable('--font-size-card-title', typography.cardTitle?.fontSize || '1.125rem', '1.125rem');
    setCSSVariable('--font-weight-card-title', typography.cardTitle?.fontWeight || '600', '600');
    setCSSVariable('--font-size-card-price', typography.cardPrice?.fontSize || '1.5rem', '1.5rem');
    setCSSVariable('--font-weight-card-price', typography.cardPrice?.fontWeight || '700', '700');
    setCSSVariable('--font-size-card-description', typography.cardDescription?.fontSize || '0.875rem', '0.875rem');
  }

  // === GLASS ===
  if (glass) {
    const enabled = glass.enabled ? '1' : '0';
    setCSSVariable('--glass-enabled', enabled, '0');
    
    const blur = glass.blur || 12;
    setCSSVariable('--glass-blur', `${blur}px`, '12px');
    setCSSVariable('--glass-blur-sm', `blur(${Math.max(4, Math.round(blur * 0.5))}px)`, 'blur(6px)');
    setCSSVariable('--glass-blur-md', `blur(${Math.max(8, Math.round(blur * 1))}px)`, 'blur(12px)');
    setCSSVariable('--glass-blur-lg', `blur(${Math.max(16, Math.round(blur * 2))}px)`, 'blur(24px)');
    
    const opacity = glass.opacity ?? 0.1;
    setCSSVariable('--glass-opacity', String(opacity), '0.1');
    
    const bg = glass.enabled 
      ? (glass.background || `rgba(255, 255, 255, ${opacity})`)
      : 'transparent';
    setCSSVariable('--glass-background', bg, 'rgba(255, 255, 255, 0.1)');
    
    const borderOpacity = glass.borderOpacity ?? 0.15;
    setCSSVariable('--glass-border-opacity', String(borderOpacity), '0.15');
    
    // === COMPLETE GLASS PROPERTIES ===
    // Glass Background
    setCSSVariable('--glass-bg-blur', String(glass.bgBlur || glass.blur || 20), '20');
    setCSSVariable('--glass-bg-opacity', String(glass.bgOpacity || glass.opacity || 0.15), '0.15');
    setCSSVariable('--glass-bg-color', glass.bgColor || glass.background || 'rgba(255,255,255,0.15)', 'rgba(255,255,255,0.15)');
    
    // Glass Card
    setCSSVariable('--glass-card-bg', glass.cardBackground || 'rgba(255,255,255,0.92)', 'rgba(255,255,255,0.92)');
    setCSSVariable('--glass-card-border', glass.cardBorder || '1px solid rgba(255,255,255,0.15)', '1px solid rgba(255,255,255,0.15)');
    setCSSVariable('--glass-card-border-radius', glass.cardBorderRadius || '16px', '16px');
    setCSSVariable('--glass-card-padding', glass.cardPadding || '16px', '16px');
    setCSSVariable('--glass-card-shadow', glass.cardShadow || '0 4px 6px rgba(0,0,0,0.1)', '0 4px 6px rgba(0,0,0,0.1)');
    setCSSVariable('--glass-card-shadow-hover', glass.cardShadowHover || '0 10px 15px rgba(0,0,0,0.15)', '0 10px 15px rgba(0,0,0,0.15)');
    setCSSVariable('--glass-card-blur', String(glass.cardBlur || 10), '10');
    
    // Glass Panel
    setCSSVariable('--glass-panel-bg', glass.panelBackground || 'rgba(255,255,255,0.85)', 'rgba(255,255,255,0.85)');
    setCSSVariable('--glass-panel-border', glass.panelBorder || '1px solid rgba(255,255,255,0.1)', '1px solid rgba(255,255,255,0.1)');
    setCSSVariable('--glass-panel-border-radius', glass.panelBorderRadius || '12px', '12px');
    setCSSVariable('--glass-panel-shadow', glass.panelShadow || '0 4px 6px rgba(0,0,0,0.08)', '0 4px 6px rgba(0,0,0,0.08)');
    setCSSVariable('--glass-panel-blur', String(glass.panelBlur || 15), '15');
    
    // Glass Modal
    setCSSVariable('--glass-modal-bg', glass.modalBackground || 'rgba(255,255,255,0.95)', 'rgba(255,255,255,0.95)');
    setCSSVariable('--glass-modal-border', glass.modalBorder || '1px solid rgba(255,255,255,0.2)', '1px solid rgba(255,255,255,0.2)');
    setCSSVariable('--glass-modal-border-radius', glass.modalBorderRadius || '20px', '20px');
    setCSSVariable('--glass-modal-shadow', glass.modalShadow || '0 25px 50px rgba(0,0,0,0.25)', '0 25px 50px rgba(0,0,0,0.25)');
    setCSSVariable('--glass-modal-padding', glass.modalPadding || '24px', '24px');
    setCSSVariable('--glass-modal-max-width', glass.modalMaxWidth || '600px', '600px');
    setCSSVariable('--glass-modal-overlay-blur', String(glass.modalOverlayBlur || 8), '8');
    
    // Glass Button
    setCSSVariable('--glass-button-bg', glass.buttonBackground || 'rgba(255,255,255,0.1)', 'rgba(255,255,255,0.1)');
    setCSSVariable('--glass-button-border', glass.buttonBorder || '1px solid rgba(255,255,255,0.2)', '1px solid rgba(255,255,255,0.2)');
    setCSSVariable('--glass-button-border-radius', glass.buttonBorderRadius || '10px', '10px');
    setCSSVariable('--glass-button-padding', glass.buttonPadding || '10px 20px', '10px 20px');
    setCSSVariable('--glass-button-shadow', glass.buttonShadow || '0 2px 4px rgba(0,0,0,0.1)', '0 2px 4px rgba(0,0,0,0.1)');
    setCSSVariable('--glass-button-shadow-hover', glass.buttonShadowHover || '0 4px 12px rgba(0,0,0,0.15)', '0 4px 12px rgba(0,0,0,0.15)');
    setCSSVariable('--glass-button-text-color', glass.buttonTextColor || '#242424', '#242424');
    
    // Glass Input
    setCSSVariable('--glass-input-bg', glass.inputBackground || 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.9)');
    setCSSVariable('--glass-input-border', glass.inputBorder || '1px solid rgba(255,255,255,0.15)', '1px solid rgba(255,255,255,0.15)');
    setCSSVariable('--glass-input-border-radius', glass.inputBorderRadius || '10px', '10px');
    setCSSVariable('--glass-input-padding', glass.inputPadding || '10px 14px', '10px 14px');
    setCSSVariable('--glass-input-shadow', glass.inputShadow || 'inset 0 1px 2px rgba(0,0,0,0.05)', 'inset 0 1px 2px rgba(0,0,0,0.05)');
    setCSSVariable('--glass-input-shadow-focus', glass.inputShadowFocus || '0 0 0 3px rgba(255,255,255,0.1)', '0 0 0 3px rgba(255,255,255,0.1)');
    setCSSVariable('--glass-input-placeholder-color', glass.inputPlaceholderColor || '#94a3b8', '#94a3b8');
    
    // Glass Header
    setCSSVariable('--glass-header-bg', glass.headerBackground || 'rgba(255,255,255,0.85)', 'rgba(255,255,255,0.85)');
    setCSSVariable('--glass-header-border', glass.headerBorder || '1px solid rgba(255,255,255,0.1)', '1px solid rgba(255,255,255,0.1)');
    setCSSVariable('--glass-header-height', glass.headerHeight || '64px', '64px');
    setCSSVariable('--glass-header-padding', glass.headerPadding || '0 24px', '0 24px');
    setCSSVariable('--glass-header-blur', String(glass.headerBlur || 12), '12');
    setCSSVariable('--glass-header-title-color', glass.headerTitleColor || '#242424', '#242424');
    setCSSVariable('--glass-header-icon-color', glass.headerIconColor || '#242424', '#242424');
    
    // Glass Sidebar
    setCSSVariable('--glass-sidebar-bg', glass.sidebarBackground || 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.9)');
    setCSSVariable('--glass-sidebar-border', glass.sidebarBorder || '1px solid rgba(255,255,255,0.1)', '1px solid rgba(255,255,255,0.1)');
    setCSSVariable('--glass-sidebar-width', glass.sidebarWidth || '280px', '280px');
    setCSSVariable('--glass-sidebar-collapsed-width', glass.sidebarCollapsedWidth || '80px', '80px');
    setCSSVariable('--glass-sidebar-padding', glass.sidebarPadding || '16px', '16px');
    setCSSVariable('--glass-sidebar-blur', String(glass.sidebarBlur || 16), '16');
    setCSSVariable('--glass-sidebar-menu-item-color', glass.sidebarMenuItemColor || '#475569', '#475569');
    setCSSVariable('--glass-sidebar-menu-item-hover-color', glass.sidebarMenuItemHoverColor || '#242424', '#242424');
    setCSSVariable('--glass-sidebar-icon-color', glass.sidebarIconColor || '#64748b', '#64748b');
    
    // Glass Bottom Nav
    setCSSVariable('--glass-bottom-nav-bg', glass.bottomNavBackground || 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.9)');
    setCSSVariable('--glass-bottom-nav-border', glass.bottomNavBorder || '1px solid rgba(255,255,255,0.1)', '1px solid rgba(255,255,255,0.1)');
    setCSSVariable('--glass-bottom-nav-height', glass.bottomNavHeight || '64px', '64px');
    setCSSVariable('--glass-bottom-nav-blur', String(glass.bottomNavBlur || 16), '16');
    setCSSVariable('--glass-bottom-nav-icon-color', glass.bottomNavIconColor || '#64748b', '#64748b');
    setCSSVariable('--glass-bottom-nav-icon-active-color', glass.bottomNavIconActiveColor || '#242424', '#242424');
    
    // Glass Chat
    setCSSVariable('--glass-chat-user-bubble', glass.chatUserBubbleColor || 'rgba(255,255,255,0.15)', 'rgba(255,255,255,0.15)');
    setCSSVariable('--glass-chat-other-bubble', glass.chatOtherBubbleColor || 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.9)');
    setCSSVariable('--glass-chat-input-bg', glass.chatInputBackground || 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.9)');
    setCSSVariable('--glass-chat-input-border', glass.chatInputBorder || '1px solid rgba(255,255,255,0.15)', '1px solid rgba(255,255,255,0.15)');
    setCSSVariable('--glass-chat-bubble-border-radius', glass.chatBubbleBorderRadius || '16px', '16px');
    setCSSVariable('--glass-chat-bubble-padding', glass.chatBubblePadding || '10px 14px', '10px 14px');
    
    // Glass Table
    setCSSVariable('--glass-table-bg', glass.tableBackground || 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.9)');
    setCSSVariable('--glass-table-border', glass.tableBorder || '1px solid rgba(255,255,255,0.1)', '1px solid rgba(255,255,255,0.1)');
    setCSSVariable('--glass-table-header-bg', glass.tableHeaderBackground || 'rgba(255,255,255,0.05)', 'rgba(255,255,255,0.05)');
    setCSSVariable('--glass-table-header-color', glass.tableHeaderColor || '#242424', '#242424');
    setCSSVariable('--glass-table-row-bg', glass.tableRowBackground || 'rgba(255,255,255,0.95)', 'rgba(255,255,255,0.95)');
    setCSSVariable('--glass-table-row-hover-bg', glass.tableRowHoverBackground || 'rgba(255,255,255,0.03)', 'rgba(255,255,255,0.03)');
    
    // Glass Badge
    setCSSVariable('--glass-badge-bg', glass.badgeBackground || 'rgba(255,255,255,0.1)', 'rgba(255,255,255,0.1)');
    setCSSVariable('--glass-badge-border-radius', glass.badgeBorderRadius || '6px', '6px');
    setCSSVariable('--glass-badge-padding-x', glass.badgePaddingX || '8px', '8px');
    setCSSVariable('--glass-badge-padding-y', glass.badgePaddingY || '4px', '4px');
    
    // Desktop/PWA specific
    setCSSVariable('--glass-desktop-blur-sm', String(glass.desktopBlurSm || 8), '8');
    setCSSVariable('--glass-desktop-blur-md', String(glass.desktopBlurMd || 20), '20');
    setCSSVariable('--glass-desktop-blur-lg', String(glass.desktopBlurLg || 40), '40');
    setCSSVariable('--glass-pwa-blur-sm', String(glass.pwaBlurSm || 6), '6');
    setCSSVariable('--glass-pwa-blur-md', String(glass.pwaBlurMd || 15), '15');
    setCSSVariable('--glass-pwa-blur-lg', String(glass.pwaBlurLg || 30), '30');
  }

  // === COLORS - Cal.com Design System ===
  const primary = colors?.primary || '#242424';
  const secondary = colors?.secondary || '#0099ff';
  const accent = colors?.accent || '#0099ff';
  const text = colors?.text || '#242424';
  const textSecondary = colors?.textSecondary || '#898989';
  const textMuted = colors?.textMuted || '#898989';
  const background = colors?.background || '#ffffff';
  const surface = colors?.surface || '#ffffff';
  const surfaceHover = colors?.surfaceHover || '#f5f5f5';
  const border = colors?.border || 'rgba(34, 42, 53, 0.08)';
  
  // Primary colors - Cal.com charcoal
  setCSSVariable('--color-primary', primary, '#242424');
  setCSSVariable('--glass-primary', primary, '#242424');
  setCSSVariable('--color-primary-hover', colors?.primaryHover || '#111111', '#111111');
  setCSSVariable('--glass-primary-hover', colors?.primaryHover || '#111111', '#111111');
  
  // Primary light for selected states
  const primaryLight = primary + '15';
  setCSSVariable('--glass-primary-light', primaryLight, '#24242415');
  
// Secondary - Cal.com link blue
  setCSSVariable('--color-secondary', secondary, '#0099ff');
  setCSSVariable('--glass-secondary', secondary, '#0099ff');
  setCSSVariable('--color-secondary-hover', colors?.secondaryHover || '#0099ff', '#0099ff');

  // Accent - Cal.com link blue
  setCSSVariable('--color-accent', accent, '#0099ff');
  setCSSVariable('--glass-accent', accent, '#0099ff');
  setCSSVariable('--color-accent-hover', colors?.accentHover || '#0099ff', '#0099ff');

  // Backgrounds - Cal.com white
  setCSSVariable('--color-background', background, '#ffffff');
  setCSSVariable('--glass-bg-primary', background, '#ffffff');
  setCSSVariable('--color-surface', surface, '#ffffff');
  setCSSVariable('--glass-secondary-bg', surface, '#ffffff');
  setCSSVariable('--color-surface-elevated', colors?.surfaceElevated || '#ffffff', '#ffffff');
  setCSSVariable('--color-surface-hover', surfaceHover, '#f5f5f5');
  setCSSVariable('--glass-hover-bg', surfaceHover, '#f5f5f5');

  // Text - Cal.com grayscale
  setCSSVariable('--color-text', text, '#242424');
  setCSSVariable('--color-text-primary', text, '#242424');
  setCSSVariable('--glass-text-primary', text, '#242424');
  setCSSVariable('--color-text-secondary', textSecondary, '#898989');
  setCSSVariable('--glass-text-secondary', textSecondary, '#898989');
  setCSSVariable('--color-text-muted', textMuted, '#898989');
  setCSSVariable('--glass-text-muted', textMuted, '#898989');
  setCSSVariable('--color-text-inverse', colors?.textInverse || '#ffffff', '#ffffff');

  // Border - Cal.com ring shadow
  setCSSVariable('--color-border', border, 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--glass-border-color', border, 'rgba(34, 42, 53, 0.08)');
  
  // State colors
  setCSSVariable('--color-success', colors?.success || '#22c55e', '#22c55e');
  setCSSVariable('--glass-success', colors?.success || '#22c55e', '#22c55e');
  setCSSVariable('--color-warning', colors?.warning || '#f59e0b', '#f59e0b');
  setCSSVariable('--glass-warning', colors?.warning || '#f59e0b', '#f59e0b');
  setCSSVariable('--color-error', colors?.error || '#ef4444', '#ef4444');
  setCSSVariable('--glass-danger', colors?.error || '#ef4444', '#ef4444');
  setCSSVariable('--color-info', colors?.info || '#0099ff', '#0099ff');
  
  // === BADGE COLORS ===
  setCSSVariable('--badge-success-bg', 'rgba(34, 197, 94, 0.15)', 'rgba(34, 197, 94, 0.15)');
  setCSSVariable('--badge-success-text', '#22c55e', '#22c55e');
  setCSSVariable('--badge-warning-bg', 'rgba(245, 158, 11, 0.15)', 'rgba(245, 158, 11, 0.15)');
  setCSSVariable('--badge-warning-text', '#f59e0b', '#f59e0b');
  setCSSVariable('--badge-error-bg', 'rgba(239, 68, 68, 0.1)', 'rgba(239, 68, 68, 0.1)');
  setCSSVariable('--badge-error-text', '#ef4444', '#ef4444');
  setCSSVariable('--badge-info-bg', 'rgba(0, 153, 255, 0.15)', 'rgba(0, 153, 255, 0.15)');
  setCSSVariable('--badge-info-text', '#0099ff', '#0099ff');
  
  // === STATE COLORS - LIGHT VARIANTS ===
  setCSSVariable('--glass-primary-light', 'rgba(0, 153, 255, 0.15)', 'rgba(0, 153, 255, 0.15)');
  setCSSVariable('--glass-success-light', 'rgba(34, 197, 94, 0.15)', 'rgba(34, 197, 94, 0.15)');
  setCSSVariable('--glass-danger-light', 'rgba(239, 68, 68, 0.1)', 'rgba(239, 68, 68, 0.1)');
  setCSSVariable('--glass-warning-light', 'rgba(245, 158, 11, 0.15)', 'rgba(245, 158, 11, 0.15)');
  
  // === TEXT COLORS ===
  setCSSVariable('--glass-text-disabled', '#9ca3af', '#9ca3af');
  setCSSVariable('--color-text-disabled', '#9ca3af', '#9ca3af');
  
  // === BACKGROUND VARIANTS ===
  setCSSVariable('--glass-secondary-bg', 'rgba(0, 0, 0, 0.02)', 'rgba(0, 0, 0, 0.02)');
  setCSSVariable('--glass-hover-bg', 'rgba(0, 0, 0, 0.04)', 'rgba(0, 0, 0, 0.04)');
  setCSSVariable('--color-bg-surface-hover', 'rgba(0, 0, 0, 0.04)', 'rgba(0, 0, 0, 0.04)');
  
  // === CHAT SPECIFIC ===
  setCSSVariable('--glass-chat-user-bubble', 'rgba(0, 153, 255, 0.15)', 'rgba(0, 153, 255, 0.15)');
  setCSSVariable('--glass-chat-other-bubble', '#ffffff', '#ffffff');
  setCSSVariable('--glass-chat-input-bg', '#ffffff', '#ffffff');
  setCSSVariable('--glass-chat-input-border', 'rgba(34, 42, 53, 0.08)', 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--glass-header-title-color', '#ffffff', '#ffffff');
  
  // === FOCUS ===
  setCSSVariable('--glass-focus-ring', 'rgba(0, 153, 255, 0.3)', 'rgba(0, 153, 255, 0.3)');
  
// === BUTTONS - Cal.com Design ===
  // Primary button - charcoal
  setCSSVariable('--btn-primary-bg', colors?.primary || '#242424', '#242424');
  setCSSVariable('--btn-primary-color', '#ffffff', '#ffffff');
  setCSSVariable('--btn-primary-hover', colors?.primaryHover || '#111111', '#111111');
  setCSSVariable('--btn-primary-shadow', 'rgba(19, 19, 22, 0.7) 0px 1px 5px -4px, rgba(34, 42, 53, 0.08) 0px 0px 0px 1px, rgba(34, 42, 53, 0.05) 0px 4px 8px 0px', 'rgba(19, 19, 22, 0.7) 0px 1px 5px -4px, rgba(34, 42, 53, 0.08) 0px 0px 0px 1px, rgba(34, 42, 53, 0.05) 0px 4px 8px 0px');
  
  // Secondary button
  setCSSVariable('--btn-secondary-bg', surface, '#ffffff');
  setCSSVariable('--btn-secondary-border', border, 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--btn-secondary-color', text, '#242424');
  setCSSVariable('--btn-secondary-hover-bg', surfaceHover, '#f5f5f5');
  setCSSVariable('--btn-secondary-hover-border', border, 'rgba(34, 42, 53, 0.08)');
  
  // === INPUT - Cal.com Design ===
  setCSSVariable('--input-bg', surface, '#ffffff');
  setCSSVariable('--input-border', border, 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--input-placeholder', textMuted, '#898989');
  setCSSVariable('--input-focus-ring', colors?.secondary || '#0099ff', '#0099ff');

  // Slider
  setCSSVariable('--glass-slider-track', border, 'rgba(34, 42, 53, 0.08)');

  // === CARD - Cal.com Multi-layer Shadow ===
  const cardBg = card?.background || '#ffffff';
  const cardBorder = card?.border || 'rgba(34, 42, 53, 0.08)';
  const cardTitle = card?.titleColor || primary;
  const cardSubtitle = card?.subtitleColor || textSecondary;
  const cardText = card?.textColor || textMuted;
  const cardShadow = card?.shadow || 'rgba(19, 19, 22, 0.7) 0px 1px 5px -4px, rgba(34, 42, 53, 0.08) 0px 0px 0px 1px, rgba(34, 42, 53, 0.05) 0px 4px 8px 0px';
  
  setCSSVariable('--glass-card-bg', cardBg, '#ffffff');
  setCSSVariable('--glass-card-border', cardBorder, 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--glass-card-border-radius', card?.borderRadius || '12px', '12px');
  setCSSVariable('--glass-card-padding', card?.padding || '16px', '16px');
  setCSSVariable('--glass-card-shadow', cardShadow, 'rgba(19, 19, 22, 0.7) 0px 1px 5px -4px, rgba(34, 42, 53, 0.08) 0px 0px 0px 1px, rgba(34, 42, 53, 0.05) 0px 4px 8px 0px');
  setCSSVariable('--glass-card-shadow-hover', card?.shadowHover || 'rgba(19, 19, 22, 0.5) 0px 4px 12px -4px, rgba(34, 42, 53, 0.08) 0px 0px 0px 1px', 'rgba(19, 19, 22, 0.5) 0px 4px 12px -4px, rgba(34, 42, 53, 0.08) 0px 0px 0px 1px');
  setCSSVariable('--glass-card-bg-hover', surfaceHover, '#f5f5f5');
  setCSSVariable('--glass-card-title-color', cardTitle, '#242424');
  setCSSVariable('--glass-card-subtitle-color', cardSubtitle, '#898989');
  setCSSVariable('--glass-card-text-color', cardText, '#898989');

  // === NAVIGATION - SIDEBAR - Cal.com ===
  const sidebarBg = navigation?.sidebar?.background || surface;
  setCSSVariable('--glass-sidebar-width', navigation?.sidebar?.width || '280px', '280px');
  setCSSVariable('--glass-sidebar-collapsed-width', navigation?.sidebar?.collapsedWidth || '80px', '80px');
  setCSSVariable('--glass-sidebar-bg', sidebarBg, '#ffffff');
  setCSSVariable('--glass-sidebar-border', navigation?.sidebar?.borderRight || border, 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--glass-sidebar-padding', '12px 16px', '12px 16px');
  setCSSVariable('--glass-sidebar-icon-color', textSecondary, '#898989');
  setCSSVariable('--glass-sidebar-text-color', text, '#242424');
  setCSSVariable('--glass-sidebar-active-icon-color', primary, '#242424');
  setCSSVariable('--glass-sidebar-active-text-color', primary, '#242424');
  setCSSVariable('--glass-sidebar-hover-bg', surfaceHover, '#f5f5f5');
  setCSSVariable('--glass-sidebar-active-bg', '#f5f5f5', '#f5f5f5');

  // === NAVIGATION - HEADER - Cal.com ===
  const headerBg = navigation?.header?.background || surface;
  setCSSVariable('--glass-header-bg', headerBg, '#ffffff');
  setCSSVariable('--glass-header-border-bottom', navigation?.header?.borderBottom || border, 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--glass-header-icon-color', textSecondary, '#898989');
  setCSSVariable('--glass-header-text-color', text, '#242424');

  // === BOAT CARD - Cal.com Colors ===
  const boatCard = theme.boatCard as any;
  if (boatCard) {
    // Image
    setCSSVariable('--boat-card-image-border-radius', boatCard.image?.borderRadius || '12px', '12px');
    setCSSVariable('--boat-card-image-aspect-ratio', boatCard.image?.aspectRatio || '16/9', '16/9');
    // Title - Cal Sans
    setCSSVariable('--boat-card-title-font-family', boatCard.title?.fontFamily || 'Cal Sans, sans-serif', 'Cal Sans, sans-serif');
    setCSSVariable('--boat-card-title-font-size', boatCard.title?.fontSize || '18px', '18px');
    setCSSVariable('--boat-card-title-font-weight', String(boatCard.title?.fontWeight || 600), '600');
    setCSSVariable('--boat-card-title-color', boatCard.title?.color || '#242424', '#242424');
    // Category - Inter
    setCSSVariable('--boat-card-category-font-family', boatCard.category?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-category-font-size', boatCard.category?.fontSize || '10px', '10px');
    setCSSVariable('--boat-card-category-font-weight', String(boatCard.category?.fontWeight || 600), '600');
    setCSSVariable('--boat-card-category-color', boatCard.category?.color || '#898989', '#898989');
    // Year
    setCSSVariable('--boat-card-year-font-family', boatCard.year?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-year-font-size', boatCard.year?.fontSize || '12px', '12px');
    setCSSVariable('--boat-card-year-color', boatCard.year?.color || '#898989', '#898989');
    // Optionals
    setCSSVariable('--boat-card-optionals-font-family', boatCard.optionals?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-optionals-font-size', boatCard.optionals?.fontSize || '8px', '8px');
    setCSSVariable('--boat-card-optionals-font-weight', String(boatCard.optionals?.fontWeight || 600), '600');
    setCSSVariable('--boat-card-optionals-color', boatCard.optionals?.color || '#16a34a', '#16a34a');
    setCSSVariable('--boat-card-optionals-background', boatCard.optionals?.background || '#f0fdf4', '#f0fdf4');
    // Dealer
    setCSSVariable('--boat-card-dealer-font-family', boatCard.dealer?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-dealer-font-size', boatCard.dealer?.fontSize || '10px', '10px');
    setCSSVariable('--boat-card-dealer-color', boatCard.dealer?.color || '#898989', '#898989');
    // Specs
    setCSSVariable('--boat-card-specs-font-family', boatCard.specs?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-specs-font-size', boatCard.specs?.fontSize || '9px', '9px');
    setCSSVariable('--boat-card-specs-color', boatCard.specs?.color || '#898989', '#898989');
    // Engine
    setCSSVariable('--boat-card-engine-font-family', boatCard.engine?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-engine-font-size', boatCard.engine?.fontSize || '9px', '9px');
    setCSSVariable('--boat-card-engine-color', boatCard.engine?.color || '#898989', '#898989');
    // Price - Charcoal
    setCSSVariable('--boat-card-price-font-family', boatCard.price?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-price-font-size', boatCard.price?.fontSize || '17px', '17px');
    setCSSVariable('--boat-card-price-font-weight', String(boatCard.price?.fontWeight || 600), '600');
    setCSSVariable('--boat-card-price-color', boatCard.price?.color || '#242424', '#242424');
    // Retail Price
    setCSSVariable('--boat-card-retail-price-font-family', boatCard.retailPrice?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-retail-price-font-size', boatCard.retailPrice?.fontSize || '10px', '10px');
    setCSSVariable('--boat-card-retail-price-color', boatCard.retailPrice?.color || '#898989', '#898989');
    // Percentage
    setCSSVariable('--boat-card-percentage-font-family', boatCard.percentage?.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    setCSSVariable('--boat-card-percentage-font-size', boatCard.percentage?.fontSize || '10px', '10px');
    setCSSVariable('--boat-card-percentage-font-weight', String(boatCard.percentage?.fontWeight || 500), '500');
    setCSSVariable('--boat-card-percentage-color', boatCard.percentage?.color || '#898989', '#898989');
  }

  // === NAVIGATION - BOTTOM NAV - Cal.com ===
  const bottomNavBg = navigation?.bottomNav?.background || surface;
  setCSSVariable('--glass-bottomnav-bg', bottomNavBg, '#ffffff');
  setCSSVariable('--glass-bottomnav-border-top', navigation?.bottomNav?.borderTop || border, 'rgba(34, 42, 53, 0.08)');
  setCSSVariable('--glass-bottomnav-height', navigation?.bottomNav?.height || '64px', '64px');
  setCSSVariable('--glass-bottomnav-icon-color', navigation?.bottomNav?.iconColor || textMuted, '#898989');
  setCSSVariable('--glass-bottomnav-icon-active-color', navigation?.bottomNav?.iconActiveColor || primary, '#242424');
  setCSSVariable('--glass-bottomnav-text-color', navigation?.bottomNav?.textColor || textSecondary, '#898989');

  // === LAYOUT - Cal.com ===
  const layoutConfig = theme.layout as any;
  if (layoutConfig) {
    setCSSVariable('--layout-max-width', layoutConfig.maxWidth || '1400px', '1400px');
    setCSSVariable('--layout-padding', layoutConfig.padding || '16px', '16px');
    setCSSVariable('--layout-grid-gap', layoutConfig.gridGap || '16px', '16px');
    setCSSVariable('--layout-container-bg', layoutConfig.containerBackground || '#ffffff', '#ffffff');
    setCSSVariable('--layout-container-border', layoutConfig.containerBorder || border, 'rgba(34, 42, 53, 0.08)');
  }

  // === TYPOGRAPHY - Cal.com (Cal Sans headings, Inter body) ===
  if (typography) {
    // Font families
    setCSSVariable('--font-family-heading', typography.fontFamilyHeading || 'Cal Sans, sans-serif', 'Cal Sans, sans-serif');
    setCSSVariable('--font-family', typography.fontFamily || 'Inter, sans-serif', 'Inter, sans-serif');
    
    // Extra small
    setCSSVariable('--text-size-xs', typography.caption?.fontSize || '0.75rem', '0.75rem');
    // Small
    setCSSVariable('--text-size-sm', typography.bodySmall?.fontSize || '0.875rem', '0.875rem');
    // Base
    setCSSVariable('--text-size-base', typography.body?.fontSize || '1rem', '1rem');
    // Large
    setCSSVariable('--text-size-lg', typography.h6?.fontSize || '1.125rem', '1.125rem');
    // XL
    setCSSVariable('--text-size-xl', typography.h5?.fontSize || '1.25rem', '1.25rem');
    // 2XL
    setCSSVariable('--text-size-2xl', typography.h4?.fontSize || '1.5rem', '1.5rem');
    // 3XL
    setCSSVariable('--text-size-3xl', typography.h3?.fontSize || '1.75rem', '1.75rem');
    
    // Font weights
    setCSSVariable('--text-weight-normal', '400', '400');
    setCSSVariable('--text-weight-medium', '500', '500');
    setCSSVariable('--text-weight-semibold', '600', '600');
    setCSSVariable('--text-weight-bold', '700', '700');
  }

  // === HEADER VARIABLES - Cal.com ===
  const headerConfig = navigation?.header as any;
  if (headerConfig) {
    setCSSVariable('--header-height', headerConfig.height || '64px', '64px');
    setCSSVariable('--header-icon-color', headerConfig.iconColor || textSecondary, '#898989');
    setCSSVariable('--header-text-color', headerConfig.textColor || text, '#242424');
  }

  // === SIDEBAR MENU ITEMS - Cal.com ===
  const sidebarConfig = navigation?.sidebar as any;
  if (sidebarConfig) {
    setCSSVariable('--sidebar-menu-item-bg', sidebarConfig.menuItemBackground || 'transparent', 'transparent');
    setCSSVariable('--sidebar-menu-item-text-color', sidebarConfig.menuItemTextColor || text, '#242424');
    setCSSVariable('--sidebar-menu-item-icon-color', sidebarConfig.menuItemIconColor || textSecondary, '#898989');
    setCSSVariable('--sidebar-menu-item-hover-bg', sidebarConfig.menuItemHoverBackground || surfaceHover, '#f5f5f5');
    setCSSVariable('--sidebar-menu-item-hover-text-color', sidebarConfig.menuItemHoverTextColor || text, '#242424');
    setCSSVariable('--sidebar-menu-item-hover-icon-color', sidebarConfig.menuItemHoverIconColor || primary, '#242424');
    setCSSVariable('--sidebar-menu-item-active-bg', sidebarConfig.menuItemActiveBackground || '#f5f5f5', '#f5f5f5');
    setCSSVariable('--sidebar-menu-item-active-text-color', sidebarConfig.menuItemActiveTextColor || primary, '#242424');
    setCSSVariable('--sidebar-menu-item-active-icon-color', sidebarConfig.menuItemActiveIconColor || primary, '#242424');
    setCSSVariable('--sidebar-section-title-color', sidebarConfig.sectionTitleColor || textMuted, '#898989');
    setCSSVariable('--sidebar-section-title-font-size', sidebarConfig.sectionTitleFontSize || '11px', '11px');
    setCSSVariable('--sidebar-padding', sidebarConfig.padding || '12px 16px', '12px 16px');
  }
  } catch (e) {
    // silent — prevent style application from crashing React commit
  }
}

/**
 * DynamicStyles component - applies theme to CSS variables
 */
export function DynamicStyles() {
  const livePreview = useThemeStore((state) => state.livePreview);
  const previewTheme = useThemeStore((state) => state.previewTheme);
  const savedTheme = useThemeStore((state) => state.savedTheme);
  
  const theme = useMemo(() => {
    const t = livePreview ? previewTheme : savedTheme;
    return t;
  }, [livePreview, previewTheme, savedTheme]);
  
  useLayoutEffect(() => {
    applyThemeVariables(theme);
  }, [theme]);
  
  return null;
}

export default DynamicStyles;
